import { Link } from "react-router-dom";
import { formatDate } from "../../api/dates";
import type { ReportRow, ReportTally, VisitReport } from "../../api/routing";
import { StatusChip, type Tone } from "../../components/ui";
import { clockTime } from "./reportQuery";

const OUTCOME: Record<ReportRow["outcome"], { label: string; tone: Tone }> = {
  done: { label: "Done", tone: "ok" },
  not_done: { label: "Not done", tone: "warn" },
  pending: { label: "Pending", tone: "quiet" },
};

/** How far a set of visits got, as a bar: done, not done, and what is left. */
function Progress({ tally }: { tally: ReportTally }) {
  if (tally.planned === 0) {
    return null;
  }
  const share = (n: number) => `${(100 * n) / tally.planned}%`;
  return (
    <span className="report-bar" aria-hidden="true">
      <span className="done" style={{ width: share(tally.done) }} />
      <span className="missed" style={{ width: share(tally.not_done) }} />
    </span>
  );
}

function TallyCells({ tally }: { tally: ReportTally }) {
  return (
    <>
      <td className="num">{tally.planned}</td>
      <td className="num">{tally.done}</td>
      <td className="num">{tally.not_done}</td>
      <td className="num">{tally.pending}</td>
      <td className="num">{tally.overdue || ""}</td>
      <td className="report-done">
        <Progress tally={tally} />
        <span>{tally.completion === null ? "—" : `${tally.completion}%`}</span>
      </td>
    </>
  );
}

function TallyHead({ first }: { first: string }) {
  return (
    <thead>
      <tr>
        <th>{first}</th>
        <th className="num">Planned</th>
        <th className="num">Done</th>
        <th className="num">Not done</th>
        <th className="num">Pending</th>
        <th className="num">Past their day</th>
        <th>Done</th>
      </tr>
    </thead>
  );
}

/** Each crew's visits, and each day's when the period has more than one. A
 * crew's name narrows the report to that crew. */
export function Breakdowns({ report, onCrew }: { report: VisitReport; onCrew: (crewId: number) => void }) {
  const crewIds = new Map(report.rows.map((row) => [row.crew, row.crew_id]));
  return (
    <div className="report-breakdowns">
      <div className="table-card">
        <table className="data-table" aria-label="By field operator">
          <TallyHead first="Field operators" />
          <tbody>
            {report.by_crew.map((crew) => {
              const id = crewIds.get(crew.crew);
              return (
                <tr key={crew.crew}>
                  <td>
                    {id !== undefined && report.filters.provider_id === null ? (
                      <button type="button" className="go-link" onClick={() => onCrew(id)}>
                        {crew.crew}
                      </button>
                    ) : (
                      crew.crew
                    )}
                  </td>
                  <TallyCells tally={crew} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {report.by_day.length > 1 ? (
        <div className="table-card">
          <table className="data-table" aria-label="By day">
            <TallyHead first="Day" />
            <tbody>
              {report.by_day.map((day) => (
                <tr key={day.date}>
                  <td>{weekdayDate(day.date)}</td>
                  <TallyCells tally={day} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function weekdayDate(iso: string): string {
  return `${new Date(`${iso}T00:00:00`).toLocaleDateString("en-ZA", { weekday: "short" })} ${formatDate(iso)}`;
}

/** Every planned visit in the period: when, who, where, and what became of it. */
export function VisitsTable({ rows }: { rows: ReportRow[] }) {
  return (
    <div className="table-card">
      <table className="data-table report-visits" aria-label="Planned visits">
        <thead>
          <tr>
            <th>Date</th>
            <th>Field operators</th>
            <th>Plan</th>
            <th>Time</th>
            <th>Site</th>
            <th>Work order</th>
            <th>Outcome</th>
            <th>Reason / note</th>
            <th>Sheet</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.stop_id} className={row.overdue ? "overdue" : undefined}>
              <td className="nowrap">{weekdayDate(row.date)}</td>
              <td>{row.crew}</td>
              <td>
                <Link to={`/routing/plans/${row.plan_id}`}>{row.plan_name}</Link>
                <span className="sub">Day {row.day_index + 1}</span>
              </td>
              <td className="num">{clockTime(row.arrive_min)}</td>
              <td>
                <Link to={`/sites/${row.site_id}`}>{row.site_name}</Link>
                {row.cluster ? <span className="sub">{row.cluster}</span> : null}
              </td>
              <td className="mono">{row.work_order ?? "—"}</td>
              <td className="nowrap">
                <StatusChip tone={OUTCOME[row.outcome].tone}>{OUTCOME[row.outcome].label}</StatusChip>
                {row.overdue ? <span className="sub report-overdue">past its day</span> : null}
              </td>
              <td>
                {row.note ?? ""}
                {row.planned_again ? <span className="sub">Planned again for {formatDate(row.planned_again)}</span> : null}
              </td>
              <td>{row.sheets ? "Filed" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
